import React, { useMemo, useEffect, useRef } from 'react'
import { Box, InputLabel, Tooltip } from '@mui/material'
import GenericButton from '../../../components/GenericButton'
import { type TruncatedDivision, type Role } from '../../../models/Role'
import GenericAutocomplete from '../../../components/FormElements/FormAutoComplete'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ButtonType } from '../../../components/GenericButton/styles'
import { grey, shade } from '../../../colors'
import { red } from '@mui/material/colors'
import { ApiAutoComplete } from '../../../components/FormElements/ApiAutoComplete'
import AttributesMultiSelect from '../../../components/FormElements/AttributesMultiSelect'
import { ATTRIBUTE_TYPES, READONLY_TOOLTIP } from '../../../consts'
import { isViewerFamilyRole } from '../../../helpers/roleHelpers'
import { useFormContext, useWatch } from 'react-hook-form'
import { type IAttribute } from '../../../models/TermsSection'
import { useAcademicUnitTypesQuery, type AcademicUnitTypeOption } from '../../../hooks/useAcademicUnitTypesQuery'
import { AcademicUnitTypeAndUnitRow, ACADEMIC_UNIT_COMBINED_HELPER } from '../../../components/AcademicUnitTypeAndUnitRow'

interface RoleCardProps {
  attributes?: IAttribute[]
  loading?: boolean
  deleteLoading?: boolean
  selectRoles?: Role[]
  deleteRole: () => void
  readOnly?: boolean
  deleteDisabled?: boolean
  selectedRole?: Role
  selectedDivision?: TruncatedDivision
  roleFieldName: string
  divisionFieldName: string
  /** Form field for academic unit type (`category_name` value), e.g. `roles[0].academicUnitCategoryName` */
  academicUnitCategoryFieldName?: string
  attributesFieldName?: string
  error?: boolean
  valueFieldForAcademicUnit?: string
}

const RoleCard: React.FC<RoleCardProps> = ({
  loading,
  selectRoles,
  deleteRole,
  readOnly = false,
  deleteDisabled = false,
  selectedRole,
  selectedDivision,
  roleFieldName,
  divisionFieldName,
  academicUnitCategoryFieldName,
  attributesFieldName,
  deleteLoading = false,
  error = false,
  valueFieldForAcademicUnit,
  attributes
}) => {
  const { control, setValue, clearErrors } = useFormContext()
  const selectedRoleId = useWatch({ control, name: roleFieldName })
  const watchCategoryName = academicUnitCategoryFieldName ?? '__academic_unit_category_absent__'
  const selectedCategory = useWatch({ control, name: watchCategoryName }) as string | undefined

  const { data: academicUnitTypes = [], isLoading: typesLoading } = useAcademicUnitTypesQuery()

  const prevCategoryRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (readOnly || !academicUnitCategoryFieldName) return
    const cat = selectedCategory ?? ''
    if (prevCategoryRef.current !== undefined && prevCategoryRef.current !== cat) {
      setValue(divisionFieldName, '', { shouldDirty: true, shouldValidate: false })
      clearErrors(divisionFieldName)
    }
    prevCategoryRef.current = cat
  }, [academicUnitCategoryFieldName, clearErrors, divisionFieldName, readOnly, selectedCategory, setValue])

  const resolvedRoleName = useMemo(() => {
    if (selectedRole?.roleName) return selectedRole.roleName
    const foundRole = selectRoles?.find(role => role.roleId === selectedRoleId)
    return foundRole?.roleName
  }, [selectedRole?.roleName, selectRoles, selectedRoleId])

  const shouldShowAttributes = isViewerFamilyRole(resolvedRoleName) && Boolean(attributesFieldName)

  // When readonly and /api/roles/ was not fetched, ensure the selectedRole is still
  // findable in the options list so GenericAutocomplete can resolve its display label.
  const roleDisplayOptions = useMemo(() => {
    if (!readOnly || !selectedRole) return selectRoles
    const alreadyPresent = selectRoles?.some(r => r.roleId === selectedRole.roleId)
    if (alreadyPresent) return selectRoles
    return [selectedRole, ...(selectRoles ?? [])]
  }, [readOnly, selectedRole, selectRoles])

  useEffect(() => {
    if (!isViewerFamilyRole(resolvedRoleName) && attributesFieldName) {
      setValue(attributesFieldName, [])
    }
  }, [attributesFieldName, setValue, resolvedRoleName])

  const typeOptions: AcademicUnitTypeOption[] = academicUnitTypes
  /** Type + unit row only when editing/creating a new role row (`academicUnitCategoryFieldName`); read-only rows show unit only. */
  const showTypeUnitCombo = Boolean(academicUnitCategoryFieldName) && !readOnly
  const hasCategoryField = Boolean(academicUnitCategoryFieldName) && !readOnly

  const typeControl = hasCategoryField
    ? (
      <GenericAutocomplete<AcademicUnitTypeOption>
        placeholder="Select type"
        textFieldStyles={{ background: grey[0] }}
        name={academicUnitCategoryFieldName as string}
        options={typeOptions}
        getOptionLabel={(opt) => opt?.categoryName ?? ''}
        getOptionValue={(opt) => opt?.categoryName ?? ''}
        loading={typesLoading}
        required={false}
        clearValue=""
      />
      )
    : null

  const unitControl = (
    <ApiAutoComplete
      apiEndpoint="/api/divisions/summary/"
      labelField="title"
      valueField={valueFieldForAcademicUnit}
      getOptionLabel={(option: any) => option?.title ?? option?.abbreviation ?? ''}
      name={divisionFieldName}
      required={false}
      placeholder="Search for academic unit"
      selectedItem={selectedDivision}
      readOnly={readOnly}
      getOptionSubLabel={(option: any) => option?.abbreviation ?? ''}
      textFieldStyles={{ background: grey[0] }}
      skip={readOnly || (hasCategoryField ? !selectedCategory : false)}
      apiParams={hasCategoryField && selectedCategory
        ? { categoryName: selectedCategory, scope: 'my' }
        : undefined}
      clearValue=""
    />
  )

  return (
    <Box display="flex" flexDirection="column" gap="24px" padding="24px" my="24px" borderRadius="8px" sx={{ border: error ? `2px solid ${red[600]}` : undefined, background: shade[750] }}>
      <GenericAutocomplete placeholder='Select Role' textFieldStyles={{ background: grey[0] }} selectedValue={selectedRole?.roleId} readOnly={readOnly} name={roleFieldName} label="Role" options={roleDisplayOptions} getOptionLabel={(role) => role?.roleName} getOptionValue={(value) => value?.roleId} loading={loading} required />
      {showTypeUnitCombo
        ? (
            <AcademicUnitTypeAndUnitRow
              groupLabel="Academic Unit"
              helperText={ACADEMIC_UNIT_COMBINED_HELPER}
              groupRequired
              typeControl={typeControl as React.ReactElement}
              unitControl={unitControl}
            />
          )
        : (
            <Box minHeight="72px">
              <InputLabel sx={{ marginBottom: 2 }}>Academic Unit</InputLabel>
              {unitControl}
            </Box>
          )}
      {shouldShowAttributes && attributesFieldName && (
        <Box>
          <AttributesMultiSelect
            name={attributesFieldName}
            // BE filters to Course + Faculty only via the array attribute_type
            // param (case middleware converts attributeType -> attribute_type).
            // includeUserAttributes=false suppresses BE's default User-row fan-out
            // when Faculty is requested.
            extraParams={{
              attributeType: [ATTRIBUTE_TYPES.COURSE_ATTRIBUTES, ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES],
              includeUserAttributes: false
            }}
            showAttributeTypeChip
            placeholder="Select Attributes"
            disabled={readOnly}
            textFieldStyles={{ background: grey[0] }}
            defaultAttributes={attributes}
          />
        </Box>
      )}
      <Tooltip title={deleteDisabled ? READONLY_TOOLTIP : ''} arrow disableHoverListener={!deleteDisabled}>
        <span style={{ alignSelf: 'flex-end' }}>
          <GenericButton onClick={deleteRole} startIcon={<DeleteOutlineIcon />} label="Delete" buttonType={ButtonType.Destructive} loading={deleteLoading} disabled={deleteDisabled} />
        </span>
      </Tooltip>
    </Box>
  )
}

export default RoleCard
